import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { signInWithCustomToken, signOut } from "firebase/auth";
import { auth } from "@/firebase/app";
import { trpc } from "#/trpc/client";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { DASHBOARD_HOME } from "@/lib/routes";
import { getEntryWorkspace } from "@/lib/workspaces";
import { useEffect, useRef } from "react";
import { pushAuthSuccessEvent } from "@/lib/gtm";
import {
  isAccountDeletedError,
  isDomainBlockedError,
} from "@/lib/trpcErrors";
import { handleAccountDeleted } from "@/lib/authErrors";

export const Route = createFileRoute("/_home/linkedin-callback")({
  component: LinkedInCallbackPage,
});

function LinkedInCallbackPage() {
  const navigate = useNavigate();
  const processedRef = useRef(false);

  const createUserMutation = useMutation({
    ...trpc.authenticatedRouterFunction.createUser.mutationOptions(),
    onSuccess: async ({ isNewUser }) => {
      pushAuthSuccessEvent({ method: "linkedin", isNewUser });
      const workspace = await getEntryWorkspace();
      if (!workspace) {
        await navigate({ to: "/" });
        return;
      }

      await navigate({
        to: DASHBOARD_HOME,
        params: { workspaceId: workspace.workspaceId },
      });
    },
    onError: async (error) => {
      if (isAccountDeletedError(error)) {
        await handleAccountDeleted(navigate);
        return;
      }
      await signOut(auth);
      if (isDomainBlockedError(error)) {
        toast.success(
          error.data.domainBlocked.waitlisted
            ? "Looks like a personal address. We've added you to the waitlist. We'll be in touch."
            : "Looks like a personal address. We'll keep you posted.",
        );
        await navigate({ to: "/" });
        return;
      }
      console.error("Create user failed:", error);
      toast.error("Login failed. Please try again.");
      await navigate({ to: "/login" });
    },
  });

  const exchangeCodeMutation = useMutation({
    ...trpc.publicRouterFunction.exchangeLinkedInCode.mutationOptions(),
    onSuccess: async (data) => {
      try {
        await signInWithCustomToken(auth, data.customToken);
      } catch {
        toast.error("LinkedIn login failed. Please try again.");
        await navigate({ to: "/login" });
        return;
      }
      createUserMutation.mutate({ email: data.email });
    },
    onError: async (error) => {
      if (error.message.includes("identity verification is required")) {
        toast.info(
          "Your LinkedIn account must be verified to sign in. We've added you to the waitlist. We'll notify you by email.",
          { duration: 10000 },
        );
        await navigate({ to: "/" });
        return;
      } else {
        toast.error("LinkedIn login failed. Please try again.");
      }
      await navigate({ to: "/login" });
    },
  });

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    const errorDescription = params.get("error_description");

    if (error) {
      toast.error(
        errorDescription ||
          "LinkedIn sign-in did not complete. Please try again.",
      );
      void navigate({ to: "/login" });
      return;
    }

    const code = params.get("code");
    const state = params.get("state");
    const storedState = sessionStorage.getItem("linkedin_oauth_state");

    if (!code || !state || state !== storedState) {
      toast.error("Invalid LinkedIn callback. Please try again.");
      void navigate({ to: "/login" });
      return;
    }

    sessionStorage.removeItem("linkedin_oauth_state");

    const redirectUri = `${window.location.origin}/linkedin-callback`;

    exchangeCodeMutation.mutate({ code, redirectUri });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="text-sm text-muted-foreground">
        Signing in with LinkedIn...
      </div>
    </div>
  );
}
